import { defineModule } from "direct-vuex";
import { Contractor } from "@/models/api/Contractor";
import { moduleActionContext, moduleGetterContext } from "@/store";
import { ChangeContractorCommand } from "@/models/api/contracts/editor/commands/ChangeContractorCommand";
import { RemoveContractorCommand } from "@/models/api/contracts/editor/commands/RemoveContractorCommand";
import { AddItemCommand } from "@/models/api/contracts/editor/commands/AddItemCommand";
import { RemoveItemCommand } from "@/models/api/contracts/editor/commands/RemoveItemCommand";
import { ChangeContractNameCommand } from "@/models/api/contracts/editor/commands/ChangeContractNameCommand";
import { ChangeContractDescriptionCommand } from "@/models/api/contracts/editor/commands/ChangeContractDescriptionCommand";
import { ChangeComponentTypeCommand } from "@/models/api/contracts/editor/commands/ChangeComponentTypeCommand";
import { CONTRACT_COMPONENT_TYPE_ENUM } from "@/static/contractComponentTypes";
import { ChangeFromDateCommand } from "@/models/api/contracts/editor/commands/ChangeFromDateCommand";
import { ChangeToDateCommand } from "@/models/api/contracts/editor/commands/ChangeToDateCommand";
import { ChangeComponentDescriptionCommand } from "@/models/api/contracts/editor/commands/ChangeComponentDescriptionCommand";
import { RemoveComponentFileCommand } from "@/models/api/contracts/editor/commands/RemoveComponentFileCommand";

const ContractsEditorCommandsModule = defineModule({
  state: {},
  namespaced: true as const,
  getters: {},
  mutations: {},
  actions: {
    async selectContractor(context, param: Contractor) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.contracts.editor.sessionId && rootState.contracts.editor.operationId) {
        const command = new ChangeContractorCommand(
          rootState.contracts.editor.sessionId,
          rootState.contracts.editor.operationId,
          {
            importedContractorId: param.id,
            city: param.city as string,
            country: param.country as string,
            surname: param.surname as string,
            isIndividual: param.isIndividual,
            name: param.name as string,
            streetAndNumber: param.streetAndNumber as string,
            tin: param.tin as string,
            postalCode: param.postalCode as string,
            nationalBusinessRegistryNumber: param.nationalBusinessRegistryNumber as string,
          }
        );
        await rootDispatch.contracts.editor.addCommandToQueue(command);
      }
    },
    async removeContractor(context) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.contracts.editor.sessionId && rootState.contracts.editor.operationId) {
        const command = new RemoveContractorCommand(
          rootState.contracts.editor.sessionId,
          rootState.contracts.editor.operationId
        );
        await rootDispatch.contracts.editor.addCommandToQueue(command);
      }
    },
    async changeContractName(context, value: string) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.contracts.editor.sessionId && rootState.contracts.editor.operationId) {
        const command = new ChangeContractNameCommand(
          rootState.contracts.editor.sessionId,
          rootState.contracts.editor.operationId,
          value
        );
        await rootDispatch.contracts.editor.addCommandToQueue(command);
      }
    },
    async changeContractDescription(context, value: string) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.contracts.editor.sessionId && rootState.contracts.editor.operationId) {
        const command = new ChangeContractDescriptionCommand(
          rootState.contracts.editor.sessionId,
          rootState.contracts.editor.operationId,
          value
        );
        await rootDispatch.contracts.editor.addCommandToQueue(command);
      }
    },
    async addItem(context) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.contracts.editor.sessionId && rootState.contracts.editor.operationId) {
        const command = new AddItemCommand(
          rootState.contracts.editor.sessionId,
          rootState.contracts.editor.operationId
        );
        await rootDispatch.contracts.editor.addCommandToQueue(command);
      }
    },
    async removeItem(context, tmpId: string) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.contracts.editor.sessionId && rootState.contracts.editor.operationId) {
        const command = new RemoveItemCommand(
          rootState.contracts.editor.sessionId,
          rootState.contracts.editor.operationId,
          tmpId
        );
        await rootDispatch.contracts.editor.addCommandToQueue(command);
      }
    },
    async changeComponentType(context, param: { tmpId: string; value: CONTRACT_COMPONENT_TYPE_ENUM }) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.contracts.editor.sessionId && rootState.contracts.editor.operationId) {
        const command = new ChangeComponentTypeCommand(
          rootState.contracts.editor.sessionId,
          rootState.contracts.editor.operationId,
          param.tmpId,
          param.value
        );
        await rootDispatch.contracts.editor.addCommandToQueue(command);
      }
    },
    async changeFromDate(context, param: { tmpId: string; value: string }) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.contracts.editor.sessionId && rootState.contracts.editor.operationId) {
        const command = new ChangeFromDateCommand(
          rootState.contracts.editor.sessionId,
          rootState.contracts.editor.operationId,
          param.tmpId,
          param.value
        );
        await rootDispatch.contracts.editor.addCommandToQueue(command);
      }
    },
    async changeToDate(context, param: { tmpId: string; value: string }) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.contracts.editor.sessionId && rootState.contracts.editor.operationId) {
        const command = new ChangeToDateCommand(
          rootState.contracts.editor.sessionId,
          rootState.contracts.editor.operationId,
          param.tmpId,
          param.value
        );
        await rootDispatch.contracts.editor.addCommandToQueue(command);
      }
    },
    async changeComponentDescription(context, param: { tmpId: string; value: string }) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.contracts.editor.sessionId && rootState.contracts.editor.operationId) {
        const command = new ChangeComponentDescriptionCommand(
          rootState.contracts.editor.sessionId,
          rootState.contracts.editor.operationId,
          param.tmpId,
          param.value
        );
        await rootDispatch.contracts.editor.addCommandToQueue(command);
      }
    },
    async removeComponentFile(context, tmpId: string) {
      const { rootDispatch, rootState } = _moduleActionContext(context);
      if (rootState.contracts.editor.sessionId && rootState.contracts.editor.operationId) {
        const command = new RemoveComponentFileCommand(
          rootState.contracts.editor.sessionId,
          rootState.contracts.editor.operationId,
          tmpId
        );
        await rootDispatch.contracts.editor.addCommandToQueue(command);
      }
    },
  },
});

export default ContractsEditorCommandsModule;

/* eslint-disable */
// noinspection JSUnusedLocalSymbols
const _moduleGetterContext = (args: [any, any, any, any]) =>
  moduleGetterContext(args, ContractsEditorCommandsModule);
const _moduleActionContext = (context: any) =>
  moduleActionContext(context, ContractsEditorCommandsModule);
